import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@classes/extends';
import { FormValidator } from '@composants/validators';
import {
    COAGCL, Organisme, OrganismeService as OrganismesService, Sig,
    Utilisateur, UtilService as UtilisateurService
} from '@estel-numerique/galaclick-api';
import { GalaclickError } from '@estel-numerique/galaclick-core';
import { Constantes } from '@services/util/constantes';
import { lastValueFrom } from 'rxjs';
import { Periode } from '../models/Periode.model';
import { DialogGalaComponent } from '../../composants/dialog-gala/dialog-gala.component';
import { SelectGala } from '../../composants/select/select.component';
import { DateGalaComponent } from '../../composants/date-gala/date-gala.component';
import { InputGala } from '../../composants/inputs/input.component';
import { MatButton } from '@angular/material/button';


export interface UtilisateurSigComponentData {
    sig: Sig;
    listeSig?: Organisme[];
    user: Utilisateur
}

@Component({
    templateUrl: './utilisateurSig.component.html',
    imports: [DialogGalaComponent, FormsModule, ReactiveFormsModule, SelectGala, DateGalaComponent, InputGala, MatButton]
})
export class UtilisateurSigComponent extends BaseComponent implements OnInit {
    private formBuilder = inject(FormBuilder);
    private wsOrga = inject(OrganismesService);
    private wsUtil = inject(UtilisateurService);
    private ref = inject(ChangeDetectorRef);
    private dialogRef = inject<MatDialogRef<UtilisateurSigComponent>>(MatDialogRef);
    public data = inject<UtilisateurSigComponentData>(MAT_DIALOG_DATA);

    public codeModule: string = Constantes.CodeOption.OFICHIERUSER;
    public titre: string;
    public listeSIG: Organisme[];
    public isUpdateSIG = false;
    private currentSIG: Sig = null;
    protected lgTrad: Record<string, string> = {};
    protected lgTradSIG: Record<string, string> = {};
    public user: Utilisateur;
    private booEdition = false;
    private type = "de l'élément SIG";
    public formSIG = this.formBuilder.group({
        SIG: [null as Organisme, [Validators.required]],
        COAGCL: ['', [Validators.required, Validators.maxLength(6), Validators.minLength(6)]],
        DDEBUT: ['', [Validators.required, FormValidator.validDate]],
        ANCIENCODE: ['', [Validators.maxLength(3), Validators.minLength(3)]],
        DFIN: ['', [FormValidator.validDate]]
    }, { validators: [this.util.date.compareDate('DDEBUT', 'DFIN')] });

    constructor() {
        super();
        this.dialogRef.updateSize('600px');
        this.lgTrad = {
            ENR: this.translate.instant('ENR'),
            RET: this.translate.instant('RET'),
        };
        this.lgTradSIG = {
            SIG: this.translate.instant('APPUTILISATEUR.SIG.SIG'),
            SIGCODE: this.translate.instant('APPUTILISATEUR.SIG.SIGCODE'),
            NAME: this.translate.instant('NAME'),
            AGENTCODE: this.translate.instant('APPUTILISATEUR.SIG.AGENTCODE'),
            DEBUT: this.translate.instant('DEBUT'),
            FIN: this.translate.instant('FIN'),
            BEGINNINGDATE: this.translate.instant('BEGINNINGDATE'),
            ENDDATE: this.translate.instant('ENDDATE'),

            CREAAGCL: this.translate.instant('APPUTILISATEUR.SIG.CREAAGCL'),
            MODIFAGCL: this.translate.instant('APPUTILISATEUR.SIG.MODIFAGCL'),

            MODIFSIG: this.translate.instant('APPUTILISATEUR.SIG.MODIFSIG'),
            DELETESIG: this.translate.instant('APPUTILISATEUR.SIG.DELETESIG'),
            ANCIENCODE: this.translate.instant('APPUTILISATEUR.SIG.ANCIENCODE')
        };
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.listeSIG = Object.assign([], this.data.listeSig);
        const dDebut = this.data.sig?.DDEBUT;
        const dFin = this.data.sig?.DFIN;
        const idSIG = this.data.sig?.IDORGA;
        const ancienCode = this.data.sig?.ANCIENCODE;
        this.user = this.data.user;
        this.booEdition = (this.user.USRID > 0);
        const debut = dDebut;
        const fin = dFin;

        //Revoir utilis&ation du formulaire car patch pas très très propre
        if (this.user.LISTESIG != null && this.user.LISTESIG.length > 0) {
            //Si le user a un déjà COAGCL
            this.formSIG.get('COAGCL').setValue(this.user.LISTESIG[0].COAGCL);
            // On veut retirer de la liste de SIG disponible à l'utilisateur ceux qui sont déjà enregistrés
            // car on ne peut avoir 2 périodes pour un meme agent et meme orga
            // Uniquement en création
            if (!dDebut) {
                this.listeSIG = Object.assign([], this.listeSIG.filter((data) =>
                    this.user.LISTESIG.find((f) => { return f.IDORGA === data.IDORGA; }) == null
                ));
            }
        }

        this.ref.detectChanges();
        if (dDebut != null || dFin != null) {
            this.isUpdateSIG = true;
            this.currentSIG = { IDORGA: idSIG, DDEBUT: dDebut, DFIN: dFin };
            this.formSIG.patchValue({
                SIG: this.data.listeSig.find((o: Organisme) => {
                    return o.IDORGA == idSIG;
                }),
                DDEBUT: debut,
                DFIN: fin,
                ANCIENCODE: ancienCode
            });

        }
    }

    public hideModalSIG() {
        this.isUpdateSIG = false;
        this.currentSIG = null;
        this.formSIG.reset();
        this.formSIG.markAsUntouched();
        this.formSIG.markAsPristine();
        this.dialogRef.close();
    }

    private markAllFieldsAsTouched(): void {
        Object.keys(this.formSIG.controls).forEach(field => {
            const control = this.formSIG.get(field);
            control?.markAsTouched({ onlySelf: true });
        });
    }


    public async addSIG() {
        if (this.formSIG.invalid) {
            this.markAllFieldsAsTouched();
            this.util.showToastError(this.translate.instant('APPUTILISATEUR.SIG.ERRORFORMSIG'));
            return;
        }
        try {
            this.util.showLoader();
            const ddebut = this.formSIG.get('DDEBUT').value;
            const dfin = this.formSIG.get('DFIN').value;

            const periode = new Periode(ddebut, dfin);
            const soustraction = periode.soustraire(new Periode(this.user.DDEBUT, this.user.DFIN));
            if (soustraction) {
                if (soustraction.length == 1) {
                    const periodeMsg = soustraction[0].formatEuropeen();
                    this.util.showToastError(
                        this.translate.instant('APPUTILISATEUR.SIG.DEPPERUTILSIMPLE')
                            .replace('{periode}', periodeMsg)
                            .replace('{type}', this.type)
                    );
                }
                else {
                    const periodeMsg = soustraction.map(p => p.formatEuropeen()).join(', ');
                    this.util.showToastError(
                        this.translate.instant('APPUTILISATEUR.SIG.DEPPERUTILMULTI')
                            .replace('{periodes}', periodeMsg)
                            .replace('{type}', this.type)
                    );
                }
                return;
            }

            if (this.formSIG.valid) {
                const coagcl = this.formSIG.get('COAGCL').value;
                if (this.formSIG.get('ANCIENCODE').value != null && this.formSIG.get('ANCIENCODE').value != '') {
                    const paramCoagcl = (this.isUpdateSIG) ? coagcl : null;
                    await lastValueFrom(this.wsUtil.checkOldCOAGCL(this.formSIG.get('ANCIENCODE').value,
                        this.formSIG.get('SIG').value.COSIG,
                        ddebut, dfin, paramCoagcl));
                }
                if (this.user.DDEBUT != null && this.user.DDEBUT > ddebut) {
                    throw new GalaclickError(this.translate.instant('APPUTILISATEUR.ERRORDATESIG'));
                }
                const dfinUtil = this.user.DFIN;
                if (dfinUtil != null) {
                    const dfinSig = this.formSIG.get('DFIN').value;
                    if (dfinSig == null) {
                        throw new GalaclickError(this.translate.instant('APPUTILISATEUR.ERRORDATESIGMANQUEFIN'));
                    } else if (dfinUtil < dfinSig) {
                        throw new GalaclickError(this.translate.instant('APPUTILISATEUR.ERRORDATESIGFINSUPP'));
                    }
                }
                const SIG: Organisme = this.formSIG.get('SIG').value;

                if (this.user.LISTESIG == null) {
                    this.user.LISTESIG = [];
                }

                // check qu'il n'y ai pas de chevauchement de SIG
                this.checkChevauchementSIG(SIG.IDORGA, SIG.COSIG, ddebut, dfin);
                // check qu'il y a possibilité de saisir une date de fin si celle ci n'est pas null
                await this.checkCurrentDFINSIG();

                if (this.isUpdateSIG) {
                    const u = this.user.LISTESIG.find((sig: Sig) => {
                        // on recupere le meme SIG que precedement (au cas ou il aurait modif les dates
                        return sig.IDORGA == this.currentSIG.IDORGA &&
                            this.currentSIG.DDEBUT == sig.DDEBUT &&
                            this.currentSIG.DFIN == sig.DFIN;
                    });
                    u.DDEBUT = ddebut;
                    u.DFIN = dfin;
                    u.ANCIENCODE = this.formSIG.get('ANCIENCODE').value;
                } else {
                    if (this.formSIG.valid) {
                        this.user.LISTESIG.push({
                            IDORGA: SIG.IDORGA,
                            NOMSIG: SIG.NOM,
                            COSIG: SIG.COSIG,
                            COAGCL: coagcl,
                            DDEBUT: ddebut,
                            DFIN: dfin,
                            ANCIENCODE: this.formSIG.get('ANCIENCODE').value
                        });
                    }
                }
                this.setListSIG();
                this.resetformSIG();
                this.hideModalSIG();
            }
        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }

    private checkChevauchementSIG(idorga: number, cosig: string, ddebut: string, dfin: string) {
        this.user.LISTESIG.forEach((sig) => {
            // verifie qu'il s'agit bien du meme organisme
            if (sig.IDORGA == idorga &&
                // verifie chevauchement date
                (this.util.date.compareTodayDate(sig.DDEBUT, sig.DFIN, true, ddebut) ||
                    (dfin != null && this.util.date.compareTodayDate(sig.DDEBUT, sig.DFIN, true, dfin)))
                // verifie que ce soit pas le SIG en cours de modif
                && (this.currentSIG == null ||
                    (this.currentSIG.DDEBUT != sig.DDEBUT && sig.DFIN != this.currentSIG.DFIN))) {
                throw new GalaclickError(this.translate.instant('APPUTILISATEUR.EXISTAFFECTATION', { libelle: cosig }));
            }

        });
    }

    public setListSIG() {
        this.listeSIG = Object.assign([], this.listeSIG);
    }

    public resetformSIG() {
        this.formSIG.patchValue({
            SIG: null
        });
        this.formSIG.markAsUntouched();
        this.formSIG.markAsPristine();
    }

    async selectCOSIG() {
        if (this.user.LISTESIG == null || this.user.LISTESIG.length == 0) {
            let code: COAGCL = null;
            let sCode: string = null;
            try {
                code = await lastValueFrom(this.wsOrga.getLastCOAGCL());
            } catch (e) {
                this.util.fErreur(e);
            }
            if (code?.CODE != null) {
                sCode = code.CODE.toString();
                const lastNumber: number = +(sCode) + 1;
                //On enleve les chiffres de fin et ajoute les nouveaux
                //On fait comme ca vu que quand c'est des code genre 0000000512 ce n'est pas un nombre Number = 512
                sCode = sCode.substring(0, sCode.length - lastNumber.toString().length);
                sCode += lastNumber.toString();
            } else {
                sCode = '000001';
            }
            this.formSIG.get('COAGCL').setValue(sCode);
        }
    }


    /**
     * Verifie la date de fin du SIG courant (celui du formSig)
     */
    public async checkCurrentDFINSIG() {
        /* Si l'utilisateur n'a pas encore été créer, il ne possède pas d'ID
        et donc il n'y a pas d'utilité de vérifier la date de fin SIG
        */
        if (this.user?.USRID) {
            const dfin = this.formSIG.get('DFIN');
            if (dfin?.value != null && dfin.value != '') {
                this.util.showLoader();
                try {
                    if (this.isUpdateSIG) {
                        const sig = {
                            IDORGA: this.formSIG.get('SIG').value.IDORGA,
                            DDEBUT: this.formSIG.get('DDEBUT').value, DFIN: dfin.value
                        };
                        await lastValueFrom(this.wsUtil.checkSuppSig(this.user.USRID, 0, sig));
                    }
                } catch (e) {
                    console.error(e);
                    throw e;
                } finally {
                    this.util.hideLoader();
                }
            }
        }

    }
}
